import React, { useCallback, useMemo } from 'react';
import {
  View,
  FlatList,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { Text } from '@/components/common/text';

import { useRoute, RouteProp } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThreadDetail } from '@/hooks/residents/corkBoard/useThreadDetail';
import { ThreadHeader } from '@/components/residents/corkBoard/ThreadHeader';
import { ThreadBody } from '@/components/residents/corkBoard/ThreadBody';
import { ReplyCard } from '@/components/residents/corkBoard/ReplyCard';
import { ReplyInput } from '@/components/residents/corkBoard/ReplyInput';
import { ThreadActionsMenu } from '@/components/residents/corkBoard/ThreadActionsMenu';
import { CorkBoardReportModal } from '@/components/residents/corkBoard/CorkBoardReportModal';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { ImageViewerModal } from '@/components/common/ImageViewerModal';
import { GradientBackground, GlassCard, SectionHeader } from '@/components/residents/redesign';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import { ThreadDetailSkeleton } from '@/components/residents/corkBoard/ThreadDetailSkeleton';
import { useAttachmentViewer } from '@/hooks/residents/corkBoard/useAttachmentViewer';
import { SurveyResponseScreen } from './SurveyResponseScreen';
import { SurveyConfirmationView } from '@/components/residents/corkBoard/survey/SurveyConfirmationView';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import type { CorkBoardReply } from '@/types/residents/corkBoard';

type ThreadDetailRouteProp = RouteProp<RootStackParamList, 'ThreadDetail'>;

const HP = REDESIGN_SPACING.horizontalPadding;

export const ThreadDetailScreen: React.FC = () => {
  const { colors, semantic } = useThemeColors();
  const route = useRoute<ThreadDetailRouteProp>();
  const { threadId } = route.params;
  const insets = useSafeAreaInsets();
  const { layoutMode } = useResponsive();

  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';

  const {
    thread,
    accumulatedReplies,
    hasMoreReplies,
    isInitialLoad,
    isFetching,
    isError,
    isSubmittingReply,
    isReporting,

    isActionsMenuVisible,
    isReportModalVisible,
    isDeleteConfirmVisible,
    isDeleteReplyConfirmVisible,
    hasReported,
    reportContentType,

    refetch,
    handleGoBack,
    handleLoadMoreReplies,
    handleSubmitReply,
    handleToggleSave,
    handleDeleteThread,
    handleDeleteReply,
    handleReportSubmit,
    handleEditThread,

    openActionsMenu,
    closeActionsMenu,
    openReportModal,
    closeReportModal,
    openDeleteConfirm,
    closeDeleteConfirm,
    openDeleteReplyConfirm,
    closeDeleteReplyConfirm,
  } = useThreadDetail(threadId);

  const {
    viewerVisible,
    viewerImages,
    viewerIndex,
    openImageViewer,
    closeImageViewer,
    openDocument,
  } = useAttachmentViewer();

  // ── Survey branching ─────────────────────────────────────────────────────
  const isSurvey = thread?.kind === 'survey';
  const surveyHasResponded = thread?.survey?.hasResponded ?? false;
  const surveyIsClosed = thread?.survey?.isClosed ?? false;
  const surveyAllowReplies = thread?.survey?.allowReplies ?? false;
  const showSurveyResponseFlow = isSurvey && !surveyHasResponded && !surveyIsClosed;
  // After responding (or for a closed survey), residents see a confirmation —
  // never the aggregate analysis, which is admin-only.
  const showSurveyConfirmation = isSurvey && !showSurveyResponseFlow;
  const showReplies = !isSurvey || surveyAllowReplies;

  // ── Reply rendering ──────────────────────────────────────────────────────
  const renderReply = useCallback(({ item }: { item: CorkBoardReply }) => (
    <View style={styles.replyWrapper}>
      <ReplyCard
        reply={item}
        isOP={thread ? item.creator.id === thread.creator.id : false}
        onDelete={item.isOwnReply ? () => openDeleteReplyConfirm(item.id) : undefined}
        onReport={!item.isOwnReply ? () => openReportModal({ replyId: Number(item.id) }) : undefined}
        onImagePress={openImageViewer}
        onDocumentPress={openDocument}
      />
    </View>
  ), [thread, openReportModal, openDeleteReplyConfirm, openImageViewer, openDocument]);

  const keyExtractor = useCallback((item: CorkBoardReply) => item.id, []);

  const ReplyCellRenderer = useCallback((props: any) => (
    <View {...props} style={[props.style, { zIndex: -props.index }]} />
  ), []);

  // ── List header (thread card + replies section header) ───────────────────
  const ListHeaderComponent = useMemo(() => {
    if (!thread || !thread.id) return null;

    return (
      <View style={styles.threadSection}>
        <GlassCard borderRadius={16} padding={20}>
          <ThreadHeader thread={thread} />

          <View style={[styles.divider, { backgroundColor: colors.separator }]} />

          <ThreadBody
            body={thread.body}
            attachments={thread.attachments}
            onImagePress={openImageViewer}
            onDocumentPress={openDocument}
          />
        </GlassCard>

        {isSurvey && showSurveyResponseFlow && (
          <SurveyResponseScreen threadId={thread.id} />
        )}

        {isSurvey && showSurveyConfirmation && (
          <SurveyConfirmationView hasResponded={surveyHasResponded} />
        )}

        {showReplies && (
          <SectionHeader title={`Replies (${thread.replyCount})`} />
        )}
      </View>
    );
  }, [
    thread,
    openImageViewer,
    openDocument,
    isSurvey,
    showSurveyResponseFlow,
    showSurveyConfirmation,
    surveyHasResponded,
    showReplies,
    colors.separator,
  ]);

  // ── List footer (loading more indicator) ─────────────────────────────────
  const ListFooterComponent = useMemo(() => {
    if (!isFetching || !hasMoreReplies) return null;
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={PRIMITIVE_COLORS.resioBlue} />
      </View>
    );
  }, [isFetching, hasMoreReplies]);

  // ── Empty replies state (glass styled) ───────────────────────────────────
  const ListEmptyComponent = useMemo(() => {
    if (isFetching) return null;
    if (!showReplies) return null;
    return (
      <View style={styles.emptyReplies}>
        <View style={[styles.emptyRepliesCard, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}>
          <RESIDENTS_SVG_COMPONENTS.CONVERSATION
            width={48}
            height={48}
            color={colors.text.dimmer}
          />
          <Text style={[styles.emptyRepliesText, { color: colors.text.muted }]}>
            No replies yet. Be the first to respond!
          </Text>
        </View>
      </View>
    );
  }, [isFetching, showReplies, colors.glass.buttonBg, colors.glass.buttonBorder, colors.text.dimmer, colors.text.muted]);

  // ── Main content renderer ────────────────────────────────────────────────
  const renderContent = (wrapInGradient: boolean) => {
    const content = (
      <View style={[
        styles.screenContainer,
        { paddingTop: isMobile || isTablet ? insets.top : 0 },
      ]}>
        {!isDesktop && <StatusBar barStyle="light-content" />}

        <KeyboardAvoidingView
          style={[
            styles.keyboardAvoidingView,
            isDesktop && styles.desktopWrapper,
          ]}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={0}
        >
          <AnimatedScreenHeader
            onBack={handleGoBack}
            onMore={thread ? openActionsMenu : undefined}
            actionsMenu={thread ? (
              <ThreadActionsMenu
                visible={isActionsMenuVisible}
                onClose={closeActionsMenu}
                isOwnThread={thread.isOwnThread}
                onEdit={handleEditThread}
                onDelete={openDeleteConfirm}
                onReport={() => openReportModal()}
                onSave={handleToggleSave}
                isSaved={thread.isSaved}
              />
            ) : undefined}
          />

          <FlatList
            data={showReplies ? accumulatedReplies : []}
            renderItem={renderReply}
            keyExtractor={keyExtractor}
            CellRendererComponent={ReplyCellRenderer}
            ListHeaderComponent={ListHeaderComponent}
            ListEmptyComponent={ListEmptyComponent}
            ListFooterComponent={ListFooterComponent}
            contentContainerStyle={styles.listContent}
            ItemSeparatorComponent={ReplySeparator}
            onEndReached={showReplies ? handleLoadMoreReplies : undefined}
            onEndReachedThreshold={0.4}
            showsVerticalScrollIndicator={false}
            removeClippedSubviews={Platform.OS !== 'web'}
            maxToRenderPerBatch={10}
            windowSize={5}
            initialNumToRender={15}
            updateCellsBatchingPeriod={50}
            scrollEventThrottle={16}
          />

          {thread && showReplies && (
            <View style={[
              styles.replyInputWrapper,
              { paddingBottom: isMobile ? insets.bottom : 0, backgroundColor: semantic.background.primary },
            ]}>
              <ReplyInput
                isLocked={thread.isLocked}
                onSubmit={handleSubmitReply}
                isSubmitting={isSubmittingReply}
              />
            </View>
          )}
        </KeyboardAvoidingView>

        {thread && (
          <>
            <CorkBoardReportModal
              visible={isReportModalVisible}
              onClose={closeReportModal}
              onSubmit={handleReportSubmit}
              isSubmitting={isReporting}
              hasAlreadyReported={hasReported}
              contentType={reportContentType}
            />

            <ConfirmationModal
              visible={isDeleteConfirmVisible}
              message="Are you sure you want to delete this thread? This action cannot be undone."
              onCancel={closeDeleteConfirm}
              onProceed={handleDeleteThread}
              cancelText="Cancel"
              proceedText="Delete"
            />

            <ConfirmationModal
              visible={isDeleteReplyConfirmVisible}
              message="Are you sure you want to delete this reply? This action cannot be undone."
              onCancel={closeDeleteReplyConfirm}
              onProceed={handleDeleteReply}
              cancelText="Cancel"
              proceedText="Delete"
            />
          </>
        )}

        <ImageViewerModal
          visible={viewerVisible}
          images={viewerImages}
          initialIndex={viewerIndex}
          onClose={closeImageViewer}
        />
      </View>
    );

    if (wrapInGradient) {
      return <GradientBackground>{content}</GradientBackground>;
    }
    return content;
  };

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (isInitialLoad) {
    const loadingContent = (
      <View style={[styles.screenContainer, { paddingTop: isMobile ? insets.top : 0 }]}>
        {!isDesktop && <StatusBar barStyle="light-content" />}
        <AnimatedScreenHeader onBack={handleGoBack} />
        <ThreadDetailSkeleton />
      </View>
    );

    if (isMobile || isTablet) {
      return <GradientBackground>{loadingContent}</GradientBackground>;
    }
    return loadingContent;
  }

  // ── Error state (glass styled) ───────────────────────────────────────────
  if (isError && !thread) {
    const errorContent = (
      <View style={[styles.screenContainer, { paddingTop: isMobile ? insets.top : 0 }]}>
        {!isDesktop && <StatusBar barStyle="light-content" />}
        <AnimatedScreenHeader onBack={handleGoBack} />
        <View style={styles.centeredContainer} accessibilityRole="alert">
          <GlassCard borderRadius={16} padding={24}>
            <View style={styles.errorCardContent}>
              <RESIDENTS_SVG_COMPONENTS.WARNING
                width={32}
                height={32}
                color={colors.text.muted}
              />
              <Text style={[styles.errorText, { color: colors.text.secondary }]}>Failed to load thread</Text>
              <TouchableOpacity
                style={styles.retryButton}
                onPress={refetch}
                accessibilityRole="button"
                accessibilityLabel="Retry loading thread"
              >
                <Text style={styles.retryButtonText}>Retry</Text>
              </TouchableOpacity>
            </View>
          </GlassCard>
        </View>
      </View>
    );

    if (isMobile || isTablet) {
      return <GradientBackground>{errorContent}</GradientBackground>;
    }
    return errorContent;
  }

  return renderContent(isMobile || isTablet);
};

// ── Sub-components ────────────────────────────────────────────────────────────

interface AnimatedScreenHeaderProps {
  onBack: () => void;
  onMore?: () => void;
  actionsMenu?: React.ReactNode;
}

const AnimatedScreenHeader: React.FC<AnimatedScreenHeaderProps> = React.memo(({
  onBack,
  onMore,
  actionsMenu,
}) => {
  const { colors } = useThemeColors();
  return (
    <View style={styles.header}>
      <ScreenBackLink onPress={onBack} />

      <View style={styles.headerTitleSpacer} />

      {onMore ? (
        <View style={styles.menuAnchor}>
          <TouchableOpacity
            style={[styles.headerButtonMore, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}
            onPress={onMore}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityRole="button"
            accessibilityLabel="Thread actions"
          >
            <RESIDENTS_SVG_COMPONENTS.THREE_DOTS width={18} height={18} color={colors.text.primary} />
          </TouchableOpacity>
          {actionsMenu}
        </View>
      ) : (
        <View style={styles.headerButtonMorePlaceholder} />
      )}
    </View>
  );
});

const ReplySeparator = () => <View style={styles.replySeparator} />;

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  desktopWrapper: {
    maxWidth: 700,
    alignSelf: 'center' as const,
    width: '100%',
    paddingTop: 24,
  },

  // ── Animated header ───────────────────────────────────────────────────
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 14,
    overflow: 'visible',
    zIndex: 10,
  },
  headerButtonMore: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerButtonMorePlaceholder: {
    width: 36,
    height: 36,
  },
  menuAnchor: {
    position: 'relative',
  },
  headerTitleSpacer: {
    flex: 1,
  },

  // ── List content ──────────────────────────────────────────────────────
  listContent: {
    paddingBottom: 16,
  },
  threadSection: {
    paddingHorizontal: HP,
    paddingTop: 24,
    gap: 0,
  },
  divider: {
    height: 1,
    marginVertical: 14,
  },

  // ── Reply items ───────────────────────────────────────────────────────
  replyWrapper: {
    paddingHorizontal: HP,
  },
  replySeparator: {
    height: 12,
  },

  // ── Footer / empty ────────────────────────────────────────────────────
  footerLoader: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  emptyReplies: {
    paddingVertical: 40,
    paddingHorizontal: HP,
    alignItems: 'center',
  },
  emptyRepliesCard: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 24,
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  emptyRepliesText: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
  },

  // ── Reply input wrapper ───────────────────────────────────────────────
  replyInputWrapper: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: 'hidden',
  },

  // ── Centered states (error) ───────────────────────────────────────────
  centeredContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: HP,
  },
  errorCardContent: {
    alignItems: 'center',
    gap: 16,
  },
  errorText: {
    ...REDESIGN_TYPOGRAPHY.sectionTitle,
  },
  retryButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
  },
  retryButtonText: {
    ...REDESIGN_TYPOGRAPHY.button,
    color: '#FFFFFF',
  },
});
